import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { ALL_TAB_PERMISSIONS, ALL_RESOURCE_PERMISSIONS, TAB_LABELS, RESOURCE_LABELS } from "@/lib/permissions";
import type { RoleInfo } from "@/types";

const PROTECTED_ROLES = ["super_admin", "admin"];

export function RolesPage() {
  const { data, isLoading } = useQuery({
    queryKey: ["roles"],
    queryFn: api.listRoles,
  });

  const [editingRole, setEditingRole] = useState<RoleInfo | null>(null);
  const roles = data?.roles ?? [];

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Sticky header */}
      <div className="shrink-0 shadow-sm bg-white px-3 py-4 sm:px-6">
        <div className="mx-auto max-w-6xl">
      <div>
        <h2 className="text-2xl font-bold tracking-tight text-near-black">Roles &amp; Permissions</h2>
        <p className="mt-1 text-sm text-text-secondary">System roles and their assigned access levels.</p>
      </div>
        </div>
      </div>

      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-6xl px-3 py-4 sm:px-6 sm:py-6">
      {isLoading ? (
        <p className="text-sm text-text-secondary">Loading roles…</p>
      ) : (
        <div className="flex flex-col gap-4">
          {roles.map((role) => (
            <RoleCard key={role.name} role={role} onEdit={PROTECTED_ROLES.includes(role.name) ? undefined : () => setEditingRole(role)} />
          ))}
        </div>
      )}
        </div>
      </div>

      {editingRole && (
        <EditRoleDialog role={editingRole} onClose={() => setEditingRole(null)} />
      )}
    </div>
  );
}

function RoleCard({ role, onEdit }: { role: RoleInfo; onEdit?: () => void }) {
  const tabPerms = role.permissions.filter((p) => p.startsWith("tab:"));
  const resourcePerms = role.permissions.filter((p) => !p.startsWith("tab:"));

  return (
    <div className="rounded-card bg-white p-5 shadow-card">
      <div className="mb-4 flex items-start justify-between border-b border-surface-secondary pb-3">
        <div>
          <h3 className="text-base font-semibold text-near-black">{role.label}</h3>
          <code className="text-[11px] text-text-secondary">{role.name}</code>
        </div>
        {onEdit && (
          <button
            onClick={onEdit}
            className="rounded-btn border border-border-card px-2.5 py-1 text-xs font-medium text-text-secondary hover:bg-surface-secondary hover:text-near-black"
          >
            Edit
          </button>
        )}
      </div>

      {tabPerms.length > 0 && (
        <div className="mb-3">
          <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.32px] text-text-secondary">
            Navigation Access
          </p>
          <div className="flex flex-wrap gap-1">
            {tabPerms.map((p) => (
              <span key={p} className="rounded-badge bg-indigo-50 px-2 py-0.5 text-[11px] font-medium text-indigo-700">
                {TAB_LABELS[p] ?? p.replace("tab:", "")}
              </span>
            ))}
          </div>
        </div>
      )}

      {resourcePerms.length > 0 && (
        <div>
          <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.32px] text-text-secondary">
            Permissions
          </p>
          <div className="flex flex-wrap gap-1">
            {resourcePerms.map((p) => {
              const cls = p.endsWith(":write")
                ? "bg-amber-50 text-amber-700"
                : p.endsWith(":read")
                ? "bg-emerald-50 text-emerald-700"
                : "bg-surface-secondary text-text-focused";
              return (
                <span key={p} className={`rounded-badge px-2 py-0.5 text-[11px] font-medium ${cls}`}>
                  {RESOURCE_LABELS[p] ?? p}
                </span>
              );
            })}
          </div>
        </div>
      )}

      {tabPerms.length === 0 && resourcePerms.length === 0 && (
        <p className="text-xs text-text-secondary">No permissions assigned.</p>
      )}
    </div>
  );
}

function EditRoleDialog({ role, onClose }: { role: RoleInfo; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<Set<string>>(new Set(role.permissions));

  const mutation = useMutation({
    mutationFn: (permissions: string[]) => api.updateRolePermissions(role.name, permissions),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["roles"] });
      onClose();
    },
  });

  function toggle(value: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(value) ? next.delete(value) : next.add(value);
      return next;
    });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="flex max-h-[85vh] w-full max-w-md flex-col rounded-card bg-white shadow-card">
        <div className="flex items-center justify-between border-b border-surface-secondary px-5 py-3">
          <div>
            <h3 className="text-lg font-semibold text-near-black">Edit Role: {role.label}</h3>
            <code className="text-[11px] text-text-secondary">{role.name}</code>
          </div>
          <button onClick={onClose} className="rounded-btn px-2 py-1 text-text-secondary hover:bg-surface-secondary hover:text-near-black">
            ✕
          </button>
        </div>

        <div className="flex-1 space-y-5 overflow-y-auto px-5 py-4">
          <PermissionGroup
            title="Navigation Access"
            items={ALL_TAB_PERMISSIONS}
            selected={selected}
            onToggle={toggle}
          />
          <PermissionGroup
            title="Permissions"
            items={ALL_RESOURCE_PERMISSIONS}
            selected={selected}
            onToggle={toggle}
          />
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-surface-secondary px-5 py-3">
          <button onClick={onClose} className="rounded-btn px-4 py-2 text-sm font-medium text-text-secondary hover:text-near-black">
            Cancel
          </button>
          <button
            onClick={() => mutation.mutate(Array.from(selected))}
            disabled={mutation.isPending}
            className="rounded-btn bg-near-black px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-rausch disabled:opacity-50"
          >
            {mutation.isPending ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

function PermissionGroup({
  title,
  items,
  selected,
  onToggle,
}: {
  title: string;
  items: { value: string; label: string }[];
  selected: Set<string>;
  onToggle: (value: string) => void;
}) {
  return (
    <div>
      <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.32px] text-text-secondary">{title}</p>
      <div className="space-y-1">
        {items.map((item) => (
          <label key={item.value} className="flex cursor-pointer items-center gap-2.5 rounded-btn px-2 py-1.5 hover:bg-surface-secondary">
            <input
              type="checkbox"
              checked={selected.has(item.value)}
              onChange={() => onToggle(item.value)}
              className="h-4 w-4 rounded border-border-card accent-near-black"
            />
            <span className="text-sm text-near-black">{item.label}</span>
            <code className="ml-auto text-[10px] text-text-secondary">{item.value}</code>
          </label>
        ))}
      </div>
    </div>
  );
}
